<template>
  <div class="breadcrumbs__wrapper">
    <div class="breadcrumbs__item text-medium">
      <a-link :to="ROUTE.HOMEPAGE">
        <a-visibility show :on="[$breakpoint.mobile]">
          <a-icon :width="16" :height="16" :icon="$options.consts.ICON.HOME" />
        </a-visibility>
        <a-visibility hide :on="[$breakpoint.mobile]">
          <span>Home</span>
        </a-visibility>
      </a-link>
    </div>
    <div
      class="breadcrumbs__item text-medium"
      v-for="(item, index) in calculatedBreadcrumbs"
      :key="item.routeName"
    >
      <a-icon
        class="breadcrumbs__divider"
        :width="16"
        :height="16"
        original
        :icon="$options.consts.ICON.ARROW_RIGHT_GREY"
      />
      <span v-if="isLastItem(index)" class="breadcrumbs__item-text">
        {{ prettifyName(item.name) }}
      </span>
      <a-link
        v-else
        :to="item.link"
        :trailing-slash="isTrailingSlashNeeded(item.link)"
        class="breadcrumbs__item-link"
      >
        {{ prettifyName(item.name) }}
      </a-link>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { propValidator, PROP_TYPES } from '@/utils/validators'
import { ROUTE } from 'enums/routes'
import AIcon, { ICON } from 'shared/AIcon'

export default {
  name: 'ABreadcrumbs',
  components: {
    AIcon
  },
  props: {
    capitalizeWords: propValidator([PROP_TYPES.BOOLEAN], false, false),
    prettifyNames: propValidator([PROP_TYPES.BOOLEAN], false, true),
    settings: propValidator([PROP_TYPES.OBJECT], false, null),
    singleLine: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  consts: {
    ICON
  },
  data() {
    return {
      ROUTE
    }
  },
  computed: {
    ...mapGetters({
      breadcrumbs: 'breadcrumbs/breadcrumbs'
    }),
    calculatedBreadcrumbs() {
      if (!this.settings) return this.breadcrumbs

      return this.breadcrumbs.map(breadcrumb => {
        const routeNameWithoutOptionalSymbol = this.getRouteNameWithoutOptionalSymbol(
          breadcrumb.routeName
        )

        const settingsForCurrentRoute = this.settings[
          routeNameWithoutOptionalSymbol
        ]

        const { nameHandler, linkHandler } = settingsForCurrentRoute || {}

        return {
          name: this.getBreadcrumbName(nameHandler, breadcrumb),
          link: this.getBreadcrumbLink(linkHandler, breadcrumb),
          routeName: breadcrumb.routeName
        }
      })
    }
  },
  methods: {
    getBreadcrumbName(nameHandler, breadcrumb) {
      return this.$helper.checkIfFunction(nameHandler)
        ? nameHandler(breadcrumb)
        : breadcrumb.name
    },
    getBreadcrumbLink(linkHandler, breadcrumb) {
      return this.$helper.checkIfFunction(linkHandler)
        ? linkHandler(breadcrumb)
        : {
            name: breadcrumb.routeName,
            params: breadcrumb.params
          }
    },
    isLastItem(index) {
      return this.calculatedBreadcrumbs.length === index + 1
    },
    prettifyName(name = '') {
      if (!this.prettifyNames) return name

      return this.capitalizeWords
        ? name
            .split(' ')
            .map(v => this.$helper.capitalize(v.toLowerCase()))
            .join(' ')
        : this.$helper.capitalize(name.toLowerCase())
    },
    getNewNameForCurrentBreadcrumb(
      breadcrumb,
      dynamicNameForCurrentBreadcrumb
    ) {
      return {
        ...breadcrumb,
        name: dynamicNameForCurrentBreadcrumb
      }
    },
    validateProps() {
      if (!this.prettifyNames && this.capitalizeWords) {
        console.error(
          'Prop validation failed:\n capitalizeWords:true prop will not be used because it is passed is passed together with the prop prettifyNames:true'
        )
      }
    },
    /**
     * Some dynamic route names might include optional parameters marked with a "?" symbol at the end.
     * It causes a route name mismatching when parsed in breadcrumbs middleware.
     * Example: Article page has the following name: "category-company-article", whereas breadcrumbs treat it
     * as "category-company-article?" which will result in undesirable inequality
     */
    getRouteNameWithoutOptionalSymbol(routeName) {
      return routeName.replace(/\?/, '')
    },
    isTrailingSlashNeeded(link) {
      return this.$helper.isString(link)
        ? this.$helper.isLinkWithTrailingSlash(link)
        : undefined
    }
  },
  mounted() {
    this.validateProps()
  }
}
</script>

<style lang="scss" scoped>
.breadcrumbs__wrapper {
  padding-bottom: 20px;
  padding-right: 20px;
  padding-left: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;

  .breadcrumbs__item {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    line-height: 16px;
    gap: 6px;

    &:last-child {
      flex-shrink: 1;
      display: flex;
      overflow: hidden;
    }

    @include mobile {
      line-height: 14px;
    }

    & > a {
      color: $c--gray-darkest;

      &:hover {
        color: $c--main;
      }
    }

    &-link,
    &-text {
      display: inline-block;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &-text {
      color: $c--gray-2;
    }
  }

  .breadcrumbs__divider {
    color: $c--gray;
  }
}
</style>
